import React, {useEffect, useState} from "react";
import {T, useTranslate} from "@tolgee/react";
import {Button, IconButton, Typography} from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import CloseIcon from "@material-ui/icons/Close";
import {IWebauthnCredentialItem} from "@evennt-tech/api-v2-external";
import {useSelector} from "react-redux";
import {usePasskeys} from "store/passkey/passkey.store";
import {selectUser} from "store/auth/selectors";
import {useShallow} from "zustand/react/shallow";
import ConfirmPasswordModal from "view/components/ConfirmPasswordModal";
import Alert from "view/components/Alert";
import RecoveryCodes from "view/components/RecoveryCodes";
import PasskeyIcon from "view/images/PasskeyIcon";
import useStyles from "./styles";

type Props = {
  onBack: () => void;
};

const formatDate = (iso: string) =>
  new Date(iso).toLocaleDateString("en-GB", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });

const PasskeyManager: React.FC<Props> = ({onBack}) => {
  const classes = useStyles();
  const {t} = useTranslate();

  const {
    credentials,
    isRegistering,
    registerErrorKey,
    recoveryCodes,
    fetchCredentials,
    requestRegistrationOptions,
    completeAddPasskey,
    revokeCredential,
    clearRegisterError,
    clearRecoveryCodes,
  } = usePasskeys(
    useShallow((state) => ({
      credentials: state.credentials,
      isRegistering: state.isRegistering,
      registerErrorKey: state.registerErrorKey,
      recoveryCodes: state.recoveryCodes,
      fetchCredentials: state.fetchCredentials,
      requestRegistrationOptions: state.requestRegistrationOptions,
      completeAddPasskey: state.completeAddPasskey,
      revokeCredential: state.revokeCredential,
      clearRegisterError: state.clearRegisterError,
      clearRecoveryCodes: state.clearRecoveryCodes,
    }))
  );

  const {hasPassword} = useSelector(selectUser);

  const [
    revokeTarget,
    setRevokeTarget,
  ] = useState<IWebauthnCredentialItem | null>(null);
  const [isConfirmingAdd, setIsConfirmingAdd] = useState(false);
  const [justAdded, setJustAdded] = useState(false);

  useEffect(() => {
    clearRegisterError();
    fetchCredentials();
    return () => clearRecoveryCodes();
  }, [clearRegisterError, fetchCredentials, clearRecoveryCodes]);

  const handleConfirmAddPasskey = async (password?: string) => {
    const options = await requestRegistrationOptions(password);
    setIsConfirmingAdd(false);
    await completeAddPasskey(options);
    if (!usePasskeys.getState().registerErrorKey) {
      setJustAdded(true);
    }
  };

  const handleConfirmRevoke = async (password?: string) => {
    if (!revokeTarget) return;
    await revokeCredential(revokeTarget.id, password);
    setRevokeTarget(null);
  };

  const renderCreatePasskeyButton = (wrapperClassName: string) => (
    <div className={wrapperClassName}>
      <Button
        variant="outlined"
        color="primary"
        startIcon={<AddIcon />}
        disabled={isRegistering || isConfirmingAdd}
        onClick={() => {
          clearRegisterError();
          if (hasPassword) {
            setIsConfirmingAdd(true);
          } else {
            handleConfirmAddPasskey();
          }
        }}
      >
        <T keyName="common.two_factor.passkey.create" />
      </Button>
    </div>
  );

  return (
    <>
      <div className={classes.backRow}>
        <IconButton size="small" onClick={onBack}>
          <ArrowBackIcon />
        </IconButton>
        <Typography className={classes.backTitle}>
          {recoveryCodes ? (
            <T keyName="common.two_factor.recovery.save_title" />
          ) : (
            <T keyName="common.two_factor.method.passkey" />
          )}
        </Typography>
      </div>

      {justAdded && (
        <div
          className={
            recoveryCodes
              ? `${classes.alertWrapper} ${classes.alertInset}`
              : classes.alertWrapper
          }
        >
          <Alert severity="success" fullWidth>
            PassKey added as a 2-Step Verification method
          </Alert>
        </div>
      )}

      {recoveryCodes ? (
        <RecoveryCodes codes={recoveryCodes} onDone={clearRecoveryCodes} />
      ) : credentials?.length ? (
        <>
          {credentials.map((credential) => (
            <div key={credential.id} className={classes.rowCard}>
              <div className={classes.iconBox}>
                <PasskeyIcon />
              </div>
              <div className={classes.rowContent}>
                <Typography className={classes.rowTitle}>
                  {credential.deviceName ||
                    t("common.two_factor.passkey.default_name")}
                </Typography>
                <Typography className={classes.rowSubtitle}>
                  {t("common.two_factor.passkey.created_on", {
                    date: formatDate(credential.createdAt),
                  })}
                </Typography>
              </div>
              <IconButton
                size="small"
                onClick={() => {
                  if (hasPassword) {
                    setRevokeTarget(credential);
                  } else {
                    revokeCredential(credential.id);
                  }
                }}
              >
                <CloseIcon />
              </IconButton>
            </div>
          ))}
          {renderCreatePasskeyButton(classes.createButtonRow)}
        </>
      ) : (
        credentials !== null && (
          <>
            <Typography variant="body2" className={classes.emptyText}>
              <T keyName="common.two_factor.passkey.description" />
            </Typography>
            {renderCreatePasskeyButton(classes.createButtonWrapper)}
          </>
        )
      )}

      {!recoveryCodes && registerErrorKey && (
        <div className={classes.alertWrapper}>
          <Alert severity="error" fullWidth>
            {t(registerErrorKey)}
          </Alert>
        </div>
      )}

      {isConfirmingAdd && (
        <ConfirmPasswordModal
          subtitle={t("common.two_factor.method.passkey")}
          description={t("common.two_factor.passkey.add_prompt")}
          submitTitle={t("common.action.continue")}
          onClose={() => setIsConfirmingAdd(false)}
          onConfirm={handleConfirmAddPasskey}
        />
      )}
      {revokeTarget && (
        <ConfirmPasswordModal
          subtitle={
            revokeTarget.deviceName ||
            t("common.two_factor.passkey.default_name")
          }
          description={t("common.two_factor.passkey.remove_prompt")}
          submitTitle={t("common.two_factor.revoke")}
          onClose={() => setRevokeTarget(null)}
          onConfirm={handleConfirmRevoke}
        />
      )}
    </>
  );
};

export default PasskeyManager;
